/**
 * Composicion visual de la tarjeta de planes.
 *
 * El endpoint de tarifas entrega cada item de `coveragesGroup` con name,
 * description, order e include, pero NO dice si el item va bajo "Coberturas" o
 * bajo "Asistencias", ni trae el texto corto que muestra el diseno ni el
 * fragmento resaltado en azul.
 *
 * Mientras backend no exponga esos datos, la clasificacion y el texto viven
 * aqui, en un unico catalogo.
 *
 * ORDEN: el campo `order` del backend NO sirve para ordenar, porque la misma
 * cobertura llega con numeros distintos segun el plan ("Pequenos accesorios y
 * llantas" es 7 en Total y Premium, pero 12 en Esencial). Por eso el orden lo
 * fija este archivo:
 * - Coberturas y Asistencias: el orden de este arreglo.
 * - "Este plan no incluye": el campo `ordenExcluido`.
 *
 * TODO: reemplazar por los campos del backend cuando existan.
 */
export type GrupoCobertura = 'cobertura' | 'asistencia';

export interface ItemComposicionPlan {
  /** `name` exacto que devuelve el backend en coveragesGroup. */
  nombreApi: string;
  /** Bloque donde se pinta cuando el plan SI la incluye. */
  grupo: GrupoCobertura;
  /** Texto en gris. */
  texto: string;
  /** Fragmento final resaltado en azul. Vacio si el item no lleva resalte. */
  destacado?: string;
  /**
   * Texto alterno para cuando el item aparece bajo "Este plan no incluye".
   * El diseno omite ahi los limites ("hasta 30 dias", "a partir del tercer
   * dia"), porque no tiene sentido acotar algo que el plan no cubre.
   * Si esta definido, reemplaza a `texto` y anula `destacado`.
   */
  textoExcluido?: string;
  /** Texto alterno cuando la moto es de alto cilindraje. */
  textoAltoCilindraje?: string;
  /** Resalte alterno cuando la moto es de alto cilindraje. */
  destacadoAltoCilindraje?: string;
  /** Posicion dentro de "Este plan no incluye". Sin valor, va al final. */
  ordenExcluido?: number;
  /**
   * Texto corto para el resumen de compra, sin limites ni resaltes.
   * Sin valor, el resumen usa `nombreApi` seguido de punto.
   */
  textoResumen?: string;
  /** Variante del texto de resumen cuando la moto es de alto cilindraje. */
  textoResumenAltoCilindraje?: string;
}

export const COMPOSICION_PLAN: ItemComposicionPlan[] = [
  // --- Coberturas ---
  {
    nombreApi: 'Robo o daño total',
    grupo: 'cobertura',
    texto: 'Robo o daño total hasta el ',
    destacado: '80% del valor de tu moto.',
  },
  {
    nombreApi: 'Daños a terceros',
    grupo: 'cobertura',
    texto: 'Daños a terceros hasta por ',
    destacado: '840 millones.',
  },
  {
    nombreApi: 'Accidentes personales al conductor',
    grupo: 'cobertura',
    texto: 'Accidentes personales al conductor.',
    ordenExcluido: 4,
  },
  {
    nombreApi: 'Renta diaria por hospitalización',
    grupo: 'cobertura',
    texto: 'Renta diaria por hospitalización a partir del tercer día y ',
    destacado: 'hasta 30 días.',
    textoExcluido: 'Renta diaria por hospitalización.',
    ordenExcluido: 6,
  },
  {
    // Nunca llega incluida: es la cobertura que se activa con el interruptor.
    nombreApi: 'Robo o daño parcial',
    grupo: 'cobertura',
    texto: 'Pérdidas parciales.',
    ordenExcluido: 8,
  },

  // --- Asistencias ---
  {
    nombreApi: 'Gastos en defensa judicial',
    grupo: 'asistencia',
    texto: 'Gastos en defensa judicial.',
  },
  {
    nombreApi: 'Asistencia jurídica virtual o en sitio',
    grupo: 'asistencia',
    texto: 'Asistencia jurídica virtual o en sitio.',
  },
  {
    nombreApi: 'Acompañamiento ante el tránsito o centros de conciliación',
    grupo: 'asistencia',
    texto: 'Acompañamiento ante el tránsito o centros de conciliación.',
    ordenExcluido: 7,
  },
  {
    nombreApi: 'Grúa en ciudad principal',
    grupo: 'asistencia',
    texto: 'Grúa en ciudad principal.',
    ordenExcluido: 1,
  },
  {
    nombreApi: 'Taller móvil',
    grupo: 'asistencia',
    texto: 'Taller móvil.',
    ordenExcluido: 2,
  },
  {
    // Pequenos Eventos. Sus subcoberturas cambian con el cilindraje:
    // bajo  -> Pequenos Accesorios y Llantas Estalladas
    // alto  -> Pequenos Accesorios, Llantas Estalladas y Rayones
    nombreApi: 'Pequeños accesorios y llantas',
    grupo: 'asistencia',
    texto: 'Pequeños accesorios: llantas estalladas, vidrios, pequeños choques y accesorios. ',
    destacado: 'Aplica para motos de bajo cilindraje.',
    textoAltoCilindraje:
      'Pequeños accesorios: llantas estalladas, rayones, vidrios, pequeños choques y accesorios. ',
    textoResumen: 'Pequeños accesorios: llantas estalladas, vidrios, pequeños choques y accesorios.',
    textoResumenAltoCilindraje:
      'Pequeños accesorios: llantas estalladas, rayones, vidrios, pequeños choques y accesorios.',
    ordenExcluido: 3,
  },
  {
    nombreApi: 'Gastos de transporte',
    grupo: 'asistencia',
    texto: 'Gastos de transporte ',
    destacado: 'hasta 30 días.',
    textoExcluido: 'Gastos de transporte.',
    ordenExcluido: 5,
  },
];

/**
 * Rayones no se pinta como un item propio: es una subcobertura de Pequenos
 * Eventos y viaja dentro del texto de "Pequenos accesorios" en alto cilindraje.
 * Por eso se descarta de las listas de la tarjeta.
 */
export const SUBCOBERTURA_EMBEBIDA_RAYONES = 'Rayones';

/**
 * Nombre con el que el backend entrega la cobertura que la tarjeta muestra
 * como "Perdidas parciales". Cuando el plan ofrece el interruptor, deja de
 * listarse bajo "Este plan no incluye" para no contradecirlo.
 */
export const NOMBRE_API_PERDIDAS_PARCIALES = 'Robo o daño parcial';

/** Tono visual del distintivo que corona cada tarjeta. */
export type TonoDistintivo = 'gris' | 'azul' | 'crema';

export interface PresentacionPlan {
  /** Texto del distintivo superior. */
  distintivo: string;
  tono: TonoDistintivo;
  /** Parrafo bajo el nombre del plan. */
  descripcion: string;
  /** Solo el plan que lo tenga pinta la franja de alto cilindraje. */
  franjaAltoCilindraje?: boolean;
}

export const PRESENTACION_PLANES: Record<string, PresentacionPlan> = {
  MOTOS_PlanEsencial: {
    distintivo: 'Más económico',
    tono: 'gris',
    descripcion:
      'Complementa tu SOAT y conduce con la tranquilidad de estar protegido en lo esencial.',
  },
  MOTOS_PlanTotal: {
    distintivo: 'Más elegido',
    tono: 'azul',
    descripcion:
      'Súmale un paso más a tu protección. Incluye todas las coberturas del Plan Esencial, más beneficios adicionales para tu día a día.',
  },
  MOTOS_PlanPremium: {
    distintivo: 'A tu medida',
    tono: 'crema',
    descripcion:
      'Protege tu moto con el plan más completo y recomendado. Descubre coberturas y beneficios exclusivos.',
    franjaAltoCilindraje: true,
  },
};

/**
 * Etiqueta con la que el resumen de compra nombra cada cobertura opcional,
 * indexada por el codigo que viaja al backend.
 */
export const ETIQUETA_COBERTURA_OPCIONAL: Record<string, string> = {
  PPD: 'Pérdida parcial',
};

/** Texto de la franja informativa que anuncia las asistencias de alto cilindraje. */
export const FRANJA_ALTO_CILINDRAJE = 'Asistencias para motos de alto cilindraje.';
